import { unstable_cache } from 'next/cache';

import { EntryCollection } from 'contentful';

import {
  CONTENT_GROUP_ACCOUNT_PAGE,
  CONTENT_TYPE_ACCOUNT_PAGE,
  CONTENT_TYPE_PRODUCT,
  CONTENT_TYPE_PRODUCT_PAGE,
  defaultLocale,
} from '@/core/constants';

import {
  TypeAccountPage,
  TypeKeyValueListFields,
  TypeProduct,
} from '@/core/generated-types';

import { filterContentfulObject } from '@/utils/helpers';

import { client, previewClient } from './contentfulClients';

import middleware from './middleware';

interface GetEntriesConfigBase {
  include?: number;
  locale?: string;
  select?: string;
  [k: `fields.${string}`]: string | undefined;
}

interface GetEntriesConfigContentType extends GetEntriesConfigBase {
  contentType?: string;
  contentTypeArray?: never;
}

interface GetEntriesConfigContentTypeArray extends GetEntriesConfigBase {
  contentType?: never;
  contentTypeArray?: string[];
}

type GetEntriesConfig =
  | GetEntriesConfigContentType
  | GetEntriesConfigContentTypeArray;

export type InventoryStatusCopy = {
  availableCta: string;
  outOfStock: string;
  outOfStockCta: string;
  outOfStockTitle: string;
  outOfStockDescription: string;
  preSale: string;
  preSaleCta: string;
  backOrder: string;
  unavailableTitle: string;
  unavailableDescription: string;
  addedToOrder: string;
};

export type ProductVariantCopy = {
  [key: string]: string;
};

export type ProductPageGeneralCopy = {
  [key: string]: string;
};

export type ContentfulPreviewData = {
  env?: string;
};

export type ContentfulLinkExternalFields = {
  entryName: string;
  linkText: string;
  ariaLabel: string;
  hasArrow: boolean;
  linkUrl: string;
  blankTarget: boolean;
  noFollow: boolean;
  hasShopSubdomain: boolean;
};

export async function getEntries<T extends object>(
  config: GetEntriesConfig,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
  shouldSanitize = true,
): Promise<EntryCollection<T>> {
  const { contentType, contentTypeArray, include, locale, select, ...fields } =
    config;

  const contentQuery = {
    ...(contentType && { content_type: contentType }),
    ...(contentTypeArray && {
      'sys.contentType.sys.id[in]': contentTypeArray.join(','),
    }),
    include: include || 10,
    locale: locale || defaultLocale,
    ...(select && { select }),
    ...fields,
  };

  try {
    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<T>(contentQuery)
      : await client.getEntries<T>(contentQuery);

    if (shouldSanitize) {
      const sanitizedEntries = middleware(entries);

      return sanitizedEntries;
    } else {
      return entries;
    }
  } catch (error) {
    console.error(error);

    throw error;
  }
}

/**
 * Cart-specific cached version of getEntries
 * Cache behavior:
 * - Lives in Next.js data cache (persists across requests)
 * - cartPageTranslations: 12 hour cache
 * - product data: 4 hour cache
 *
 * Cache invalidation:
 * - Automatic: After revalidate time expires
 * - Manual: Redeploy app or use revalidateTag('contentful')
 * - On-demand: Use Next.js revalidatePath('/cart') or revalidateTag()
 */
export async function getCartEntries<T extends object>({
  config,
  isPreview = false,
  previewData,
  shouldSanitize = true,
}: {
  config: GetEntriesConfig;
  isPreview?: boolean;
  previewData?: ContentfulPreviewData;
  shouldSanitize?: boolean;
}): Promise<EntryCollection<T>> {
  // Don't cache preview requests
  if (isPreview) {
    return getEntries<T>(config, isPreview, previewData, shouldSanitize);
  }

  // Create a cache key based on the config
  const cacheKey = `cart-contentful-${JSON.stringify(config)}-${shouldSanitize}`;
  const oneWeekInSeconds = 604800;

  // Determine cache duration based on content type
  let revalidate = 3600; // Default: 1 hour

  // Cart page translations rarely change - cache longer
  if (config.contentType === 'cartPageTranslations') {
    revalidate = oneWeekInSeconds * 2; // 2 weeks
  } else if (
    config.contentType === CONTENT_TYPE_PRODUCT.PRODUCT ||
    config.contentType === CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE
  ) {
    revalidate = oneWeekInSeconds; // 1 week for product data
  }

  const cachedFn = unstable_cache(
    async () => getEntries<T>(config, isPreview, previewData, shouldSanitize),
    [cacheKey],
    {
      revalidate,
      tags: [
        'cart-contentful',
        config.contentType || 'contentful',
        `locale-${config.locale || defaultLocale}`,
      ],
    },
  );

  return cachedFn();
}

export function getEntriesByPageType<T extends Record<string, any>>(
  config: GetEntriesConfig,
  pageTypes: object,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const { locale, ...fields } = config;

  const contentQuery = {
    locale: locale ?? '*',
    ...fields,
  };

  const promises: Promise<EntryCollection<T>>[] = [];

  for (const pageType of Object.values(pageTypes)) {
    promises.push(
      getEntries<T>(
        {
          ...contentQuery,
          contentType: pageType,
        },
        isPreview,
        previewData,
      ),
    );
  }

  return Promise.all(promises);
}

export async function getAccountPage(
  targetPageGroup: CONTENT_GROUP_ACCOUNT_PAGE,
  locale: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const page = await getEntriesByPageType(
    {
      locale,
      'fields.contentGroup': targetPageGroup,
    },
    CONTENT_TYPE_ACCOUNT_PAGE,
    isPreview,
    previewData,
  );

  const queriedPages = page?.[0].items;

  const targetPage = queriedPages?.[0] as TypeAccountPage;

  return filterContentfulObject(targetPage);
}

export async function getKeyValueLists(
  ids: string[],
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<object | null> {
  try {
    const contentQuery = {
      content_type: 'keyValueList',
      locale: locale || defaultLocale,
      'fields.id[in]': ids.join(',') || '',
    };

    const entries: EntryCollection<TypeKeyValueListFields> = isPreview
      ? await previewClient(previewData?.env).getEntries(contentQuery)
      : await client.getEntries(contentQuery);

    if (!entries) {
      return null;
    }

    // There is more than likely a more performant and elegant way to achieve this using, for example, the .reduce() method.
    const mappedKeyValues: any = {};

    for (let i = 0; i < ids.length; i++) {
      const iteratedId = ids[i];

      mappedKeyValues[iteratedId] = null; // We initialize a null property for this ID.

      for (let j = 0; j < entries.items.length; j++) {
        const iteratedEntryFields = entries.items[j]?.fields;

        if (iteratedEntryFields?.id === iteratedId) {
          if (!mappedKeyValues[iteratedId]) {
            mappedKeyValues[iteratedId] = {}; // Since we have a match, make the property a nested object.
          }

          for (let k = 0; k < iteratedEntryFields?.keyValues.length; k++) {
            const keyValue = iteratedEntryFields?.keyValues[k];
            mappedKeyValues[iteratedId][keyValue.key] = keyValue.value;
          }
        }
      }
    }

    return mappedKeyValues;
  } catch (error) {
    console.error(error);

    return null;
  }
}

export async function getInventoryStatusCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<InventoryStatusCopy | null> {
  const inventoryStatusCopy = (await getKeyValueLists(
    ['inventoryStatuses'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return (inventoryStatusCopy?.inventoryStatuses ||
    null) as InventoryStatusCopy | null;
}

export async function getProductVariantsCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const productVariantsCopy = (await getKeyValueLists(
    ['productVariantTranslations'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return productVariantsCopy?.productVariantTranslations || null;
}

export async function getProductPageGeneralCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const productPageGeneralCopy = (await getKeyValueLists(
    ['productPageTranslations'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return productPageGeneralCopy?.productPageTranslations || null;
}

export type GetProductsByPlaygeniusProductAnchorsProps = {
  anchors: string[];
  locale?: string;
  isPreview?: boolean;
  previewData?: ContentfulPreviewData;
  include?: number;
};

export const getProductsByPlaygeniusProductAnchors = async ({
  anchors,
  locale,
  include = 1,
  isPreview,
  previewData,
}: GetProductsByPlaygeniusProductAnchorsProps): Promise<TypeProduct[]> => {
  try {
    const contentQuery = {
      'fields.playgeniusProductAnchor[in]': anchors.join(','),
      include,
      content_type: 'product',
      locale: locale || defaultLocale,
    };

    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<TypeProduct>(
          contentQuery,
        )
      : await client.getEntries<TypeProduct>(contentQuery);

    return (entries.items as unknown as TypeProduct[]) || [];
  } catch (error) {
    console.error(error);

    return [];
  }
};

export const getProductByPlaygeniusProductAnchor = async (
  playgeniusProductAnchor: string,
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<TypeProduct> => {
  try {
    const contentQuery = {
      'fields.playgeniusProductAnchor': playgeniusProductAnchor,
      content_type: 'product',
      locale: locale || defaultLocale,
    };

    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<TypeProduct>(
          contentQuery,
        )
      : await client.getEntries<TypeProduct>(contentQuery);

    return (entries.items[0] as unknown as TypeProduct) || null;
  } catch (error) {
    console.error(error);

    throw error;
  }
};

export const getContentfulObjectFromItems = <T extends { fields: any }>(
  items: T[],
  fieldsKey: keyof T['fields'],
) =>
  items.reduce(
    (acc, item) => {
      const key = item.fields[fieldsKey];

      return {
        ...acc,
        [key]: item,
      };
    },
    {} as Record<string, T>,
  );
